import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Menu, X, CheckCircle, BookOpen, Users, Award, 
  Clock, Video, Headphones, MessageCircle, ChevronDown,
  Mail, Phone, MapPin, Send, Play, Star, Shield, Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp config - Número real
const WHATSAPP_NUMBER = "50663169520";
const WHATSAPP_MESSAGE = "Hola! Estoy interesado en el curso Cisco CCNA. Me gustaría recibir más información.";

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_web-completa-1/artifacts/fhyb9734_AT%20logo%20blanco%20fondo%20transparente.png";
const LOGO_FOOTER_URL = "https://customer-assets.emergentagent.com/job_web-completa-1/artifacts/zdn7hld0_AT%20logo%20fondo%20transparente.png";

// Images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_web-completa-1/artifacts/0fapwg15_Imagen%20para%20CCNA.png";
const SERVER_IMAGE = "https://customer-assets.emergentagent.com/job_web-completa-1/artifacts/y4a76qhy_Logo%20Academia%20AT.jpeg";
const CCNA_IMAGE = "https://customer-assets.emergentagent.com/job_web-completa-1/artifacts/grbxtwly_CCNA%20Course.png";
const LINUX_IMAGE = "https://customer-assets.emergentagent.com/job_web-completa-1/artifacts/3z0vl8zn_Linux%20course.png";
const AWS_IMAGE = "https://customer-assets.emergentagent.com/job_web-completa-1/artifacts/q5jd9e43_AWS%20Course1.png";
const AZURE_IMAGE = "https://customer-assets.emergentagent.com/job_web-completa-1/artifacts/azpbgrr1_Azure%20course.png";

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#inicio", label: "Inicio" },
    { href: "#cursos", label: "Cursos" },
    { href: "#beneficios", label: "Beneficios" },
    { href: "#precios", label: "Precios" },
    { href: "#faq", label: "FAQ" },
    { href: "#contacto", label: "Contacto" },
  ];

  return (
    <nav className={`navbar ${isScrolled ? "scrolled" : ""}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a href="#inicio" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-28 h-16 flex items-center justify-center">
              <img 
                src={LOGO_URL} 
                alt="Academia Tecnológica" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-xl font-bold text-white hidden sm:block">Academia Tecnológica</span>
          </a>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-slate-300 hover:text-cyan-400 transition-colors font-medium"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm"
              data-testid="nav-whatsapp-btn"
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden glass rounded-xl mt-4 p-4 animate-fade-in" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 text-slate-300 hover:text-cyan-400 transition-colors font-medium border-b border-slate-700 last:border-0"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full justify-center mt-4 text-sm"
            >
              <MessageCircle size={18} />
              Contactar por WhatsApp
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section - Fondo Azul Oscuro
const HeroSection = () => {
  return (
    <section id="inicio" className="min-h-screen flex items-center pt-20 relative overflow-hidden bg-navy" data-testid="hero-section">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-light to-navy opacity-90"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white" data-testid="hero-title">
              Curso <span className="gradient-text">Cisco CCNA</span>
              <br />
              <span className="text-slate-300 text-3xl sm:text-4xl lg:text-5xl">+ Laboratorios + Simulación de Examen</span>
            </h1>
            
            <p className="text-slate-400 text-lg mb-8 max-w-xl" data-testid="hero-description">
              Prepárate para Cisco CCNA con clases en vivo, laboratorios prácticos y simulación de examen. 
              Acceso a grabaciones y soporte durante todo el proceso. 
              <strong className="text-white"> Ideal si querés mejorar tu perfil y conseguir oportunidades en redes.</strong>
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-lg"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={20} />
                Quiero información por WhatsApp
              </a>
              <a href="#cursos" className="btn-secondary text-lg" data-testid="hero-temario-btn">
                <BookOpen size={20} />
                Ver el temario
              </a>
            </div>
            
            <div className="flex items-center gap-8 mt-10">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-cyan-400" size={20} />
                <span className="text-slate-300">100% Online</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-cyan-400" size={20} />
                <span className="text-slate-300">Clases en Vivo</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-cyan-400" size={20} />
                <span className="text-slate-300">Certificación</span>
              </div>
            </div>
          </div>
          
          <div className="hidden lg:block animate-float">
            <div className="relative">
              <div className="glass rounded-3xl p-8">
                <img 
                  src={HERO_IMAGE} 
                  alt="Networking Equipment" 
                  className="rounded-2xl w-full"
                />
                <div className="absolute -bottom-6 -right-6 glass rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center">
                    <Play size={24} className="text-navy ml-1" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">+180 horas</p>
                    <p className="text-slate-400 text-sm">de contenido</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Benefits Section - Fondo Blanco
const BenefitsSection = () => {
  const benefits = [
    {
      icon: <Video size={32} />,
      title: "Clases en Vivo + Grabaciones",
      description: "Asiste a clases en tiempo real o revisá las grabaciones cuando quieras. Acceso 24/7."
    },
    {
      icon: <Shield size={32} />,
      title: "Laboratorios Prácticos",
      description: "Practicá con equipos virtualizados de Cisco. Experiencia real antes del examen."
    },
    {
      icon: <Award size={32} />,
      title: "Simulación de Examen",
      description: "Preparate con simulacros idénticos al examen oficial de certificación CCNA."
    },
    {
      icon: <Headphones size={32} />,
      title: "Soporte Personalizado",
      description: "Resolvé tus dudas con nuestros instructores certificados durante todo el curso."
    },
    {
      icon: <Users size={32} />,
      title: "Comunidad Activa",
      description: "Conectá con otros estudiantes y profesionales del área de networking."
    },
    {
      icon: <BookOpen size={32} />,
      title: "Material Actualizado",
      description: "Contenido alineado al examen CCNA 200-301 vigente con actualizaciones incluidas."
    }
  ];

  return (
    <section id="beneficios" className="section bg-white" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-title text-navy" data-testid="benefits-title">
            ¿Por qué elegir <span className="gradient-text">Academia Tecnológica</span>?
          </h2>
          <p className="section-subtitle mx-auto text-gray-600">
            Todo lo que necesitás para aprobar el examen CCNA en un solo lugar
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index} 
              className="card-white group"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`benefit-card-${index}`}
            >
              <div className="w-16 h-16 bg-cyan-500/10 rounded-2xl flex items-center justify-center text-cyan-600 mb-6 group-hover:bg-cyan-500 group-hover:text-white transition-all">
                {benefit.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3 text-navy">{benefit.title}</h3>
              <p className="text-gray-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Courses Section - Fondo Azul Oscuro
const CoursesSection = () => {
  const courses = [
    {
      title: "Cisco CCNA 200-301",
      badge: "PRINCIPAL",
      description: "Certificación completa en routing, switching y fundamentos de red",
      topics: [
        "Fundamentos de Redes",
        "Acceso a la Red",
        "Conectividad IP",
        "Servicios IP",
        "Fundamentos de Seguridad",
        "Automatización y Programabilidad"
      ],
      duration: "10 meses",
      available: true,
      image: CCNA_IMAGE
    },
    {
      title: "Linux LPIC-1",
      badge: "PRÓXIMAMENTE",
      description: "Administración de sistemas Linux para profesionales IT",
      topics: [
        "Instalar y configurar sistemas Linux desde cero",
        "Administrar usuarios, permisos y servicios",
        "Gestionar almacenamiento, procesos y recursos del sistema",
        "Automatizar tareas con scripts en Bash",
        "Configurar redes y conectividad en Linux",
        "Trabajar con logs, seguridad y monitoreo",
        "Dominar comandos esenciales para administración"
      ],
      duration: "2 meses",
      available: false,
      image: LINUX_IMAGE
    },
    {
      title: "AWS Cloud Practitioner",
      badge: "PRÓXIMAMENTE",
      description: "Fundamentos de Amazon Web Services",
      topics: ["Conceptos de nube", "Servicios principales", "Seguridad", "Facturación"],
      duration: "2 meses",
      available: false,
      image: AWS_IMAGE
    },
    {
      title: "Microsoft Azure",
      badge: "PRÓXIMAMENTE",
      description: "Administración de servicios en la nube de Microsoft",
      topics: ["Azure fundamentals", "Máquinas virtuales", "Almacenamiento", "Redes"],
      duration: "2 meses",
      available: false,
      image: AZURE_IMAGE
    }
  ];

  return (
    <section id="cursos" className="section bg-navy" data-testid="courses-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-title text-white" data-testid="courses-title">
            Nuestros <span className="gradient-text">Cursos</span>
          </h2>
          <p className="section-subtitle mx-auto text-slate-400">
            Formación especializada en tecnologías de redes y cloud
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {courses.map((course, index) => (
            <div 
              key={index}
              className={`card relative ${course.available ? 'border-cyan-500/50' : ''}`}
              data-testid={`course-card-${index}`}
            >
              <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${
                course.available 
                  ? 'bg-cyan-500 text-navy' 
                  : 'bg-slate-600 text-slate-300'
              }`}>
                {course.badge}
              </div>
              
              {course.image && (
                <img src={course.image} alt={course.title} className="w-full h-56 object-cover rounded-xl mb-4" />
              )}
              
              <h3 className="text-2xl font-bold mb-3 text-white">{course.title}</h3>
              <p className="text-slate-400 mb-6">{course.description}</p>
              
              <div className="flex items-center gap-2 mb-4 text-cyan-400">
                <Clock size={18} />
                <span>Duración: {course.duration}</span>
              </div>
              
              <h4 className="text-sm font-semibold text-slate-300 mb-3">CONTENIDO:</h4>
              <ul className="space-y-2 mb-6">
                {course.topics.map((topic, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-400">
                    <CheckCircle size={16} className="text-cyan-400 flex-shrink-0" />
                    {topic}
                  </li>
                ))}
              </ul>
              
              {course.available ? (
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hola! Quiero inscribirme al curso ${course.title}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full justify-center"
                  data-testid={`course-${index}-cta`}
                >
                  <Send size={18} />
                  Inscribirme
                </a>
              ) : (
                <button className="btn-secondary w-full justify-center opacity-50 cursor-not-allowed" disabled>
                  Próximamente
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section - Fondo Blanco
const PricingSection = () => {
  const plans = [
    {
      name: "Módulo Individual",
      price: "$200",
      period: "USD / módulo",
      features: [
        "Acceso a 1 módulo del curso",
        "Clases en vivo + grabaciones",
        "Acceso a laboratorios",
        "Soporte personalizado",
        "Un año de acceso",
        "Certificado de finalización"
      ],
      popular: false
    },
    {
      name: "Curso Completo CCNA",
      price: "$500",
      period: "USD",
      features: [
        "3 módulos completos",
        "Clases en vivo + grabaciones",
        "Laboratorios",
        "Simulación de examen",
        "Soporte personalizado",
        "Un año de acceso",
        "Certificado de finalización"
      ],
      popular: true
    },
    {
      name: "Curso de Linux",
      price: "$100",
      period: "USD",
      features: [
        "Todo el Curso Completo",
        "Acceso por un año",
        "Soporte personalizado"
      ],
      popular: false
    }
  ];

  return (
    <section id="precios" className="section bg-white" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-title text-navy" data-testid="pricing-title">
            Planes y <span className="gradient-text">Precios</span>
          </h2>
          <p className="section-subtitle mx-auto text-gray-600">
            Elegí el plan que mejor se adapte a tus necesidades
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`card-white relative ${plan.popular ? 'border-2 border-cyan-500 scale-105 shadow-xl' : ''}`}
              data-testid={`pricing-card-${index}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                  MÁS POPULAR
                </div>
              )}
              
              <h3 className="text-xl font-semibold mb-2 text-navy">{plan.name}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold gradient-text">{plan.price}</span>
                <span className="text-gray-500 ml-2">{plan.period}</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600">
                    <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hola! Estoy interesado en el plan ${plan.name}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full justify-center ${plan.popular ? 'btn-primary' : 'btn-secondary-dark'}`}
                data-testid={`pricing-${index}-cta`}
              >
                Reservar cupo
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section - Fondo Azul Oscuro
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Jose Pablo Piedra Vargas",
      date: "31 de mayo de 2025",
      text: "Paciencia y dedicación es lo que hace que sus clases sean excelentes, explican al detalle los temas y abordan cualquier consulta.",
      source: "Facebook"
    },
    {
      name: "Daniela Araya",
      date: "14 de abril de 2025",
      text: "Las clases son muy dinámicas, los profesores enseñan muy bien y responden todas las dudas, cada instructor cuenta con mucho conocimiento en el área. 100% recomendados.",
      source: "Facebook"
    },
    {
      name: "Esthelita Nataly Tenelema",
      date: "14 de abril de 2025",
      text: "Excelente la Academia, el profe Miguel un crack para explicar y llegar a que el estudiante comprenda el material impartido, yo le quedo agradecida por toda la enseñanza, y lo recomiendo de sobremanera...",
      source: "Facebook"
    },
    {
      name: "Jarod Vasquez Salas",
      date: "10 de octubre de 2024",
      text: "Clases muy bien explicadas, de una forma en la cual podemos entender bien el material impartido. Las clases con el profesor Miguel son bastante buenas, el uso de sus analogías y la paciencia al explicar temas algo complicados es muy buena.",
      source: "Facebook"
    },
    {
      name: "Yordan A Gonzalez",
      date: "10 de octubre de 2024",
      text: "Excelente academia, ayuda a los estudiantes hasta con la más mínima pregunta, siempre responde a las dudas, y me ayudó mucho para poder ir aprendiendo y tener material de apoyo para poder conseguir trabajo en lo que me gusta que es la tecnología.",
      source: "Facebook"
    }
  ];

  return (
    <section className="section bg-navy" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-title text-white" data-testid="testimonials-title">
            Lo que dicen nuestros <span className="gradient-text">Estudiantes</span>
          </h2>
          <p className="section-subtitle mx-auto text-slate-400">
            Reseñas reales de estudiantes en Facebook
          </p>
        </div>
        
        {/* First row - 3 testimonials */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <div key={index} className="card flex flex-col" data-testid={`testimonial-card-${index}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {testimonial.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-white text-sm">{testimonial.name}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-cyan-400 text-xs">recomienda</span>
                    <span className="text-slate-400 text-xs">Academia Tecnológica</span>
                  </div>
                </div>
              </div>
              <p className="text-slate-300 text-sm flex-1 leading-relaxed">"{testimonial.text}"</p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                <span className="text-slate-500 text-xs">{testimonial.date}</span>
                <div className="flex items-center gap-1 text-blue-400">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="text-xs">Facebook</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Second row - 2 testimonials centered */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-10">
          {testimonials.slice(3, 5).map((testimonial, index) => (
            <div key={index + 3} className="card flex flex-col" data-testid={`testimonial-card-${index + 3}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {testimonial.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-white text-sm">{testimonial.name}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-cyan-400 text-xs">recomienda</span>
                    <span className="text-slate-400 text-xs">Academia Tecnológica</span>
                  </div>
                </div>
              </div>
              <p className="text-slate-300 text-sm flex-1 leading-relaxed">"{testimonial.text}"</p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                <span className="text-slate-500 text-xs">{testimonial.date}</span>
                <div className="flex items-center gap-1 text-blue-400">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="text-xs">Facebook</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Facebook CTA */}
        <div className="text-center">
          <a
            href="https://www.facebook.com/share/15JxNZHJto/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-full transition-all hover:scale-105"
            data-testid="facebook-cta"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Ver más reseñas en Facebook
          </a>
        </div>
      </div>
    </section>
  );
};

// FAQ Section - Fondo Blanco
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "¿Cuánto dura el curso CCNA?",
      answer: "El curso completo tiene una duración de 10 meses, en promedio 3 meses cada módulo con clases en vivo 2 veces por semana. Todas las clases quedan grabadas para que puedas verlas cuando quieras."
    },
    {
      question: "¿Necesito conocimientos previos?",
      answer: "No es necesario tener conocimientos previos en redes. Empezamos desde los fundamentos básicos. Sin embargo, es recomendable tener conocimientos básicos de informática y manejo de computadoras."
    },
    {
      question: "¿Qué modalidad tiene el curso?",
      answer: "El curso es 100% online. Tenés clases en vivo por Zoom, acceso a grabaciones, laboratorios virtuales con equipos Cisco, material de estudio en PDF y simulaciones de examen."
    },
    {
      question: "¿El curso garantiza aprobar el examen?",
      answer: "Nuestro programa está diseñado para prepararte completamente para el examen CCNA 200-301. Incluimos simulacros de examen y seguimiento personalizado. La tasa de aprobación de nuestros estudiantes es del 85%."
    },
    {
      question: "¿Cómo son los laboratorios prácticos?",
      answer: "Utilizamos Packet Tracer para simular redes reales de Cisco. Nuestros profesores enseñan ejercicios prácticos que replican escenarios del mundo real."
    },
    {
      question: "¿Puedo pagar en cuotas?",
      answer: "Sí, ofrecemos opciones de pago en cuotas pero queda a criterio del director académico. También aceptamos pagos internacionales vía PayPal y transferencia bancaria. Consultanos por WhatsApp para más opciones."
    },
    {
      question: "¿Qué soporte ofrecen durante el curso?",
      answer: "Tenés acceso a un grupo de WhatsApp exclusivo para estudiantes, y clases en vivo donde le pueden hacer consultas a los instructores."
    }
  ];

  return (
    <section id="faq" className="section bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-title text-navy" data-testid="faq-title">
            Preguntas <span className="gradient-text">Frecuentes</span>
          </h2>
          <p className="section-subtitle mx-auto text-gray-600">
            Resolvemos tus dudas más comunes
          </p>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="card-white cursor-pointer border border-gray-200"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              data-testid={`faq-item-${index}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-navy pr-4">{faq.question}</h3>
                <ChevronDown 
                  size={24} 
                  className={`text-cyan-500 flex-shrink-0 transition-transform ${openIndex === index ? 'rotate-180' : ''}`}
                />
              </div>
              {openIndex === index && (
                <p className="mt-4 text-gray-600 animate-fade-in">{faq.answer}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section - Fondo Azul Oscuro
const ContactSection = () => {
  const [formData, setFormData] = useState({
    nombre: "",
    whatsapp: "",
    email: "",
    curso_interes: "CCNA 200-301",
    mensaje: ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Construir mensaje para WhatsApp
    const mensajeWhatsApp = `¡Hola! Me interesa información sobre Academia Tecnológica.

*Mis datos:*
📌 Nombre: ${formData.nombre}
📱 WhatsApp: ${formData.whatsapp}
📧 Email: ${formData.email}
📚 Curso de interés: ${formData.curso_interes}
${formData.mensaje ? `💬 Mensaje: ${formData.mensaje}` : ''}`;

    // Abrir WhatsApp con el mensaje
    const urlWhatsApp = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mensajeWhatsApp)}`;
    window.open(urlWhatsApp, '_blank');
    
    // Limpiar formulario
    setFormData({
      nombre: "",
      whatsapp: "",
      email: "",
      curso_interes: "CCNA 200-301",
      mensaje: ""
    });
  };

  return (
    <section id="contacto" className="section bg-navy" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="section-title text-white" data-testid="contact-title">
              ¿Listo para <span className="gradient-text">empezar</span>?
            </h2>
            <p className="section-subtitle mb-8 text-slate-400">
              Completá el formulario y te contactaremos por WhatsApp para darte toda la información que necesités.
            </p>
            
            <div className="space-y-6">
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-slate-300 hover:text-cyan-400 transition-colors"
                data-testid="contact-whatsapp"
              >
                <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                  <MessageCircle size={24} className="text-cyan-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">WhatsApp</p>
                  <p>+506 6316 9520</p>
                </div>
              </a>
              
              <a 
                href="mailto:info@academiatecnologica.net"
                className="flex items-center gap-4 text-slate-300 hover:text-cyan-400 transition-colors"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                  <Mail size={24} className="text-cyan-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">Email</p>
                  <p>info@academiatecnologica.net</p>
                </div>
              </a>
              
              <div className="flex items-center gap-4 text-slate-300">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                  <Clock size={24} className="text-cyan-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">Horario de atención</p>
                  <p>Lunes a Viernes: 9:00 - 21:00 hs</p>
                </div>
              </div>
            </div>
            
            {/* Logo Academia */}
            <div className="mt-8 hidden lg:block">
              <img 
                src={SERVER_IMAGE} 
                alt="Academia Tecnológica" 
                className="rounded-2xl w-full max-w-md"
              />
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center gap-2 mb-6 text-green-400">
              <MessageCircle size={20} />
              <span className="text-sm font-medium">Te responderemos por WhatsApp</span>
            </div>
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Nombre completo *</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Tu nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    required
                    data-testid="form-nombre"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">WhatsApp *</label>
                  <input
                    type="tel"
                    className="input-field"
                    placeholder="+506 1234 5678"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                    required
                    data-testid="form-whatsapp"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="form-email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Curso de interés *</label>
                  <select
                    className="input-field"
                    value={formData.curso_interes}
                    onChange={(e) => setFormData({...formData, curso_interes: e.target.value})}
                    required
                    data-testid="form-curso"
                  >
                    <option value="CCNA 200-301">Cisco CCNA 200-301</option>
                    <option value="Linux">Linux LPIC-1 (próximamente)</option>
                    <option value="AWS">AWS Cloud (próximamente)</option>
                    <option value="Azure">Microsoft Azure (próximamente)</option>
                    <option value="Otro">Otro / Consulta general</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Mensaje (opcional)</label>
                  <textarea
                    className="input-field min-h-[100px] resize-none"
                    placeholder="¿Tenés alguna consulta específica?"
                    value={formData.mensaje}
                    onChange={(e) => setFormData({...formData, mensaje: e.target.value})}
                    data-testid="form-mensaje"
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  className="w-full justify-center bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-6 rounded-full transition-all hover:scale-105 flex items-center gap-2"
                  data-testid="form-submit"
                >
                  <svg viewBox="0 0 32 32" className="w-5 h-5" fill="currentColor">
                    <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.132 6.742 3.052 9.378L1.054 31.29l6.166-1.966A15.89 15.89 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.32 22.598c-.39 1.098-1.924 2.01-3.13 2.276-.826.18-1.904.322-5.534-1.19-4.646-1.932-7.636-6.654-7.868-6.962-.224-.308-1.876-2.498-1.876-4.764s1.186-3.38 1.608-3.844c.39-.43.926-.644 1.442-.644.174 0 .33.016.47.03.422.018.634.042.912.706.348.83 1.194 2.912 1.298 3.124.106.212.212.494.086.784-.118.298-.222.43-.434.69-.212.26-.414.46-.626.738-.194.244-.412.506-.176.928.236.414 1.052 1.736 2.26 2.812 1.552 1.382 2.86 1.81 3.266 2.012.308.152.674.126.918-.144.308-.34.688-.902 1.076-1.456.276-.394.624-.442.964-.298.346.136 2.188 1.032 2.564 1.22.376.188.626.28.718.436.09.156.09.902-.3 2.002z"/>
                  </svg>
                  Enviar consulta por WhatsApp
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer - Fondo Blanco
const Footer = () => {
  return (
    <footer className="bg-gray-50 py-12 border-t border-gray-200" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <img src={LOGO_FOOTER_URL} alt="Academia Tecnológica" className="w-full h-full object-contain" />
            </div>
            <span className="font-semibold text-navy">Academia Tecnológica</span>
          </div>
          
          <div className="flex items-center gap-6 text-gray-600">
            <a href="#inicio" className="hover:text-cyan-500 transition-colors">Inicio</a>
            <a href="#cursos" className="hover:text-cyan-500 transition-colors">Cursos</a>
            <a href="#precios" className="hover:text-cyan-500 transition-colors">Precios</a>
            <a href="#contacto" className="hover:text-cyan-500 transition-colors">Contacto</a>
          </div>
          
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/share/15JxNZHJto/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center text-white transition-colors"
              aria-label="Facebook"
              data-testid="footer-facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center text-white transition-colors"
              aria-label="WhatsApp"
              data-testid="footer-whatsapp"
            >
              <svg viewBox="0 0 32 32" className="w-5 h-5" fill="currentColor">
                <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.132 6.742 3.052 9.378L1.054 31.29l6.166-1.966A15.89 15.89 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.32 22.598c-.39 1.098-1.924 2.01-3.13 2.276-.826.18-1.904.322-5.534-1.19-4.646-1.932-7.636-6.654-7.868-6.962-.224-.308-1.876-2.498-1.876-4.764s1.186-3.38 1.608-3.844c.39-.43.926-.644 1.442-.644.174 0 .33.016.47.03.422.018.634.042.912.706.348.83 1.194 2.912 1.298 3.124.106.212.212.494.086.784-.118.298-.222.43-.434.69-.212.26-.414.46-.626.738-.194.244-.412.506-.176.928.236.414 1.052 1.736 2.26 2.812 1.552 1.382 2.86 1.81 3.266 2.012.308.152.674.126.918-.144.308-.34.688-.902 1.076-1.456.276-.394.624-.442.964-.298.346.136 2.188 1.032 2.564 1.22.376.188.626.28.718.436.09.156.09.902-.3 2.002z"/>
              </svg>
            </a>
          </div>
        </div>
        
        <div className="mt-8 pt-8 border-t border-gray-200 text-center">
          <p className="text-gray-500 text-sm">
            © 2026 Academia Tecnológica. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Float Button
const WhatsAppFloat = () => {
  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      data-testid="whatsapp-float"
      aria-label="Contactar por WhatsApp"
    >
      <svg viewBox="0 0 32 32" width="32" height="32" fill="white">
        <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.132 6.742 3.052 9.378L1.054 31.29l6.166-1.966A15.89 15.89 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.32 22.598c-.39 1.098-1.924 2.01-3.13 2.276-.826.18-1.904.322-5.534-1.19-4.646-1.932-7.636-6.654-7.868-6.962-.224-.308-1.876-2.498-1.876-4.764s1.186-3.38 1.608-3.844c.39-.43.926-.644 1.442-.644.174 0 .33.016.47.03.422.018.634.042.912.706.348.83 1.194 2.912 1.298 3.124.106.212.212.494.086.784-.118.298-.222.43-.434.69-.212.26-.414.46-.626.738-.194.244-.412.506-.176.928.236.414 1.052 1.736 2.26 2.812 1.552 1.382 2.86 1.81 3.266 2.012.308.152.674.126.918-.144.308-.34.688-.902 1.076-1.456.276-.394.624-.442.964-.298.346.136 2.188 1.032 2.564 1.22.376.188.626.28.718.436.09.156.09.902-.3 2.002z"/>
      </svg>
    </a>
  );
};

// Main App
function App() {
  return (
    <div className="App min-h-screen" data-testid="app-container">
      <Navbar />
      <HeroSection />
      <BenefitsSection />
      <CoursesSection />
      <PricingSection />
      <TestimonialsSection />
      <FAQSection />
      <ContactSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
